import * as THREE from 'three';
import type { Random } from '../../core/Random';
import { box, cyl, M, mat, mesh, registerNightGlow, sphere, texMat } from '../materials';
import { signTexture, woodTexture } from '../textures';

/**
 * Indoor furniture and wall hangings. Everything faces +z (its front) and sits on y = 0,
 * like the yard props. Small parts are kept at least half a centimeter off any face they
 * sit on, so nothing flickers.
 */

const laminate = (): THREE.MeshStandardMaterial => mat('#d9cfb4', { rough: 0.5 });
const cabinet = (): THREE.MeshStandardMaterial => texMat(woodTexture(), { color: '#b8875a' });
const almond = (): THREE.MeshStandardMaterial => mat('#ece5d2', { rough: 0.45 });

export function bed(blanket: string, size: 'double' | 'twin' = 'double'): THREE.Group {
  const g = new THREE.Group();
  const w = size === 'double' ? 1.5 : 1.0;
  g.add(box(w + 0.08, 0.3, 2.05, cabinet(), 0, 0.15, 0));
  g.add(box(w, 0.22, 1.95, mat('#f1ece0'), 0, 0.41, 0.02));
  g.add(box(w + 0.02, 0.08, 1.4, mat(blanket, { rough: 0.95 }), 0, 0.56, 0.3));
  for (const x of size === 'double' ? [-0.36, 0.36] : [0])
    g.add(box(0.55, 0.12, 0.32, mat('#fbf8f0'), x, 0.58, -0.72));
  g.add(box(w + 0.12, 1.05, 0.1, cabinet(), 0, 0.53, -1.07));
  return g;
}

export function waterbed(): { group: THREE.Group; water: THREE.Mesh } {
  const g = new THREE.Group();
  g.add(box(1.9, 0.42, 2.25, cabinet(), 0, 0.21, 0));
  const water = mesh(
    new THREE.BoxGeometry(1.7, 0.16, 2.05),
    mat('#3b7fb8', { rough: 0.15, metal: 0.2 }),
    0,
    0.48,
    0,
  );
  g.add(water);
  g.add(box(2.0, 1.2, 0.12, cabinet(), 0, 0.6, -1.17));
  for (const x of [-0.5, 0.5]) g.add(box(0.6, 0.12, 0.35, mat('#fbf8f0'), x, 0.62, -0.82));
  return { group: g, water };
}

export function dresser(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(1.1, 0.85, 0.5, cabinet(), 0, 0.425, 0));
  for (let i = 0; i < 3; i++) {
    g.add(box(1.0, 0.22, 0.02, mat('#a5764b'), 0, 0.17 + i * 0.26, 0.26, { cast: false }));
    g.add(box(0.12, 0.03, 0.03, M.chrome, 0, 0.2 + i * 0.26, 0.285, { cast: false }));
  }
  return g;
}

export interface BuiltTv {
  group: THREE.Group;
  screen: THREE.MeshStandardMaterial;
  /** The glass: a plane whose UVs cover the picture, for showing a program on it. */
  screenMesh: THREE.Mesh;
}

export function crtTv(): BuiltTv {
  const g = new THREE.Group();
  g.add(box(1.0, 0.5, 0.45, cabinet(), 0, 0.25, 0));
  g.add(box(0.72, 0.56, 0.52, mat('#2b2b2e', { rough: 0.6 }), 0, 0.78, -0.02));
  const screen = new THREE.MeshStandardMaterial({
    color: '#1b2a2e',
    roughness: 0.2,
    emissive: new THREE.Color('#5a8fb0'),
    emissiveIntensity: 0.15,
  });
  const screenMesh = mesh(new THREE.PlaneGeometry(0.56, 0.42), screen, -0.03, 0.8, 0.245, { cast: false });
  g.add(screenMesh);
  for (const s of [-1, 1]) {
    const ear = cyl(0.008, 0.008, 0.45, M.chrome, s * 0.1, 1.2, -0.05, 4);
    ear.rotation.z = s * -0.5;
    g.add(ear);
  }
  return { group: g, screen, screenMesh };
}

export function flatTv(): BuiltTv {
  const g = new THREE.Group();
  g.add(box(1.8, 0.45, 0.45, cabinet(), 0, 0.225, 0));
  g.add(box(1.6, 0.92, 0.06, mat('#111114', { rough: 0.3 }), 0, 1.0, 0));
  const screen = new THREE.MeshStandardMaterial({
    color: '#0d1418',
    roughness: 0.15,
    emissive: new THREE.Color('#6aa6d8'),
    emissiveIntensity: 0.12,
  });
  const screenMesh = mesh(new THREE.PlaneGeometry(1.5, 0.84), screen, 0, 1.0, 0.035, { cast: false });
  g.add(screenMesh);
  g.add(box(0.3, 0.1, 0.2, mat('#111114'), 0, 0.5, 0));
  return { group: g, screen, screenMesh };
}

export function coffeeTable(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(1.1, 0.06, 0.6, cabinet(), 0, 0.42, 0));
  for (const x of [-0.48, 0.48])
    for (const z of [-0.24, 0.24]) g.add(box(0.06, 0.39, 0.06, cabinet(), x, 0.195, z));
  // Ashtray, remote and a can.
  g.add(cyl(0.08, 0.07, 0.03, mat('#8a8f94', { metal: 0.6 }), -0.3, 0.465, 0.05, 10));
  g.add(box(0.18, 0.025, 0.06, M.black, 0.15, 0.462, -0.1, { cast: false }));
  g.add(cyl(0.035, 0.035, 0.12, mat('#c0392b', { metal: 0.6, rough: 0.3 }), 0.35, 0.51, 0.12, 8));
  return g;
}

export function kitchenCounter(length: number, withSink: boolean): THREE.Group {
  const g = new THREE.Group();
  g.add(box(length, 0.86, 0.6, cabinet(), 0, 0.43, 0));
  g.add(box(length + 0.04, 0.05, 0.64, laminate(), 0, 0.885, 0.01));
  const doors = Math.max(1, Math.round(length / 0.6));
  for (let i = 0; i < doors; i++) {
    const x = -length / 2 + (i + 0.5) * (length / doors);
    g.add(box(length / doors - 0.06, 0.7, 0.02, mat('#a5764b'), x, 0.42, 0.305, { cast: false }));
  }
  if (withSink) {
    g.add(box(0.6, 0.02, 0.42, mat('#9ea4a8', { metal: 0.7, rough: 0.25 }), 0, 0.915, 0, { cast: false }));
    g.add(cyl(0.02, 0.02, 0.28, M.chrome, 0, 1.05, -0.24, 6));
    const spout = box(0.03, 0.03, 0.18, M.chrome, 0, 1.18, -0.16, { cast: false });
    g.add(spout);
  }
  return g;
}

export function stove(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.76, 0.9, 0.64, almond(), 0, 0.45, 0));
  for (const x of [-0.18, 0.18])
    for (const z of [-0.14, 0.14]) g.add(cyl(0.11, 0.11, 0.02, M.black, x, 0.91, z, 10, { cast: false }));
  g.add(box(0.6, 0.38, 0.02, mat('#2e2a26', { rough: 0.3 }), 0, 0.42, 0.325, { cast: false }));
  g.add(box(0.76, 0.18, 0.06, almond(), 0, 1.0, -0.29));
  // A pot of something.
  g.add(cyl(0.12, 0.11, 0.14, mat('#8a8f94', { metal: 0.6 }), 0.18, 0.99, 0.14, 10));
  return g;
}

export function homeFridge(): THREE.Group {
  const g = new THREE.Group();
  const body = almond();
  g.add(box(0.78, 1.75, 0.7, body, 0, 0.875, 0));
  g.add(box(0.76, 0.02, 0.02, mat('#b9b2a0'), 0, 1.2, 0.355, { cast: false }));
  for (const y of [0.75, 1.5]) g.add(box(0.04, 0.32, 0.05, M.chrome, -0.3, y, 0.38, { cast: false }));
  // Magnets and a bill nobody's paying.
  const colors = ['#e63946', '#ffd166', '#06d6a0', '#3a86ff'];
  for (let i = 0; i < 4; i++)
    g.add(
      box(
        0.07,
        0.07,
        0.02,
        mat(colors[i] as string),
        0.05 + (i % 2) * 0.15,
        1.35 - Math.floor(i / 2) * 0.2,
        0.36,
        {
          cast: false,
        },
      ),
    );
  g.add(box(0.2, 0.26, 0.01, mat('#fbf8f0'), 0.15, 0.9, 0.358, { cast: false }));
  return g;
}

export function dinette(chairColor: string): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.48, 0.48, 0.04, laminate(), 0, 0.74, 0, 16));
  g.add(cyl(0.05, 0.05, 0.72, M.chrome, 0, 0.36, 0, 8));
  g.add(cyl(0.28, 0.3, 0.03, M.chrome, 0, 0.015, 0, 12));
  const vinyl = mat(chairColor, { rough: 0.5 });
  for (const s of [-1, 1]) {
    const c = new THREE.Group();
    c.add(box(0.42, 0.06, 0.42, vinyl, 0, 0.46, 0));
    c.add(box(0.42, 0.42, 0.06, vinyl, 0, 0.72, -0.19));
    for (const x of [-0.18, 0.18])
      for (const z of [-0.18, 0.18]) c.add(box(0.03, 0.43, 0.03, M.chrome, x, 0.215, z));
    c.position.set(s * 0.72, 0, 0);
    c.rotation.y = (s * -Math.PI) / 2;
    g.add(c);
  }
  return g;
}

export function toilet(): THREE.Group {
  const g = new THREE.Group();
  const porcelain = mat('#f6f4ee', { rough: 0.25 });
  g.add(box(0.36, 0.38, 0.5, porcelain, 0, 0.19, 0.05));
  g.add(box(0.42, 0.06, 0.55, porcelain, 0, 0.41, 0.07));
  g.add(box(0.48, 0.42, 0.2, porcelain, 0, 0.63, -0.24));
  // A magazine rack: one issue of Bass Fishing Monthly from 2006.
  g.add(box(0.05, 0.3, 0.3, mat('#6b4a33'), 0.36, 0.15, 0.1));
  return g;
}

export function bathtub(): THREE.Group {
  const g = new THREE.Group();
  const porcelain = mat('#f6f4ee', { rough: 0.25 });
  g.add(box(1.6, 0.5, 0.75, porcelain, 0, 0.25, 0));
  g.add(box(1.44, 0.02, 0.6, mat('#bfd6dc', { rough: 0.1 }), 0, 0.505, 0, { cast: false }));
  g.add(cyl(0.025, 0.025, 0.2, M.chrome, -0.7, 0.62, 0, 6));
  // Shower curtain with ducks on it.
  g.add(box(1.6, 1.4, 0.02, mat('#ffe27a', { rough: 0.9 }), 0, 1.4, 0.39, { cast: false }));
  return g;
}

export function bathroomSink(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.6, 0.8, 0.45, cabinet(), 0, 0.4, 0));
  g.add(box(0.64, 0.04, 0.48, laminate(), 0, 0.82, 0.01));
  g.add(box(0.44, 0.02, 0.32, mat('#f6f4ee'), 0, 0.85, 0.02, { cast: false }));
  g.add(box(0.5, 0.6, 0.03, mat('#cfe3ea', { rough: 0.05, metal: 0.6 }), 0, 1.45, -0.21));
  return g;
}

export function floorLamp(): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.16, 0.16, 0.03, M.black, 0, 0.015, 0, 10));
  g.add(cyl(0.02, 0.02, 1.4, M.black, 0, 0.72, 0, 6));
  const shade = new THREE.MeshStandardMaterial({
    color: '#f3dfae',
    emissive: new THREE.Color('#ffc96b'),
    emissiveIntensity: 0.7,
    side: THREE.DoubleSide,
  });
  g.add(mesh(new THREE.CylinderGeometry(0.16, 0.24, 0.3, 12, 1, true), shade, 0, 1.45, 0, { cast: false }));
  return g;
}

export function rug(w: number, d: number, color: string): THREE.Mesh {
  const r = mesh(new THREE.PlaneGeometry(w, d), mat(color, { rough: 1 }), 0, 0.012, 0, { cast: false });
  r.rotation.x = -Math.PI / 2;
  return r;
}

/** A welcome mat with an opinion. */
export function doorMat(): THREE.Mesh {
  const tex = signTexture({
    w: 256,
    h: 128,
    bg: '#6b4a2b',
    fg: '#e8d8b0',
    border: '#3d2a18',
    borderWidth: 10,
    lines: [{ text: 'GO AWAY', size: 70, font: 'Anton' }],
  });
  const m = mesh(new THREE.PlaneGeometry(0.9, 0.45), texMat(tex), 0, 0.014, 0, { cast: false });
  m.rotation.x = -Math.PI / 2;
  return m;
}

/** The inside of the front door, set into a wall. */
export function interiorDoor(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(1.0, 2.1, 0.06, mat('#efe9da'), 0, 1.05, 0));
  g.add(box(0.86, 1.98, 0.05, mat('#e2d9c2'), 0, 1.0, 0.04));
  g.add(box(0.06, 0.06, 0.05, M.chrome, 0.32, 0.98, 0.09, { cast: false }));
  // Chain lock. Has never been used.
  g.add(box(0.12, 0.02, 0.02, M.chrome, 0.3, 1.45, 0.08, { cast: false }));
  return g;
}

/** A window seen from inside: the view is a glowing pane, framed by curtains. */
export function interiorWindow(pane: THREE.Material, curtain: string, w = 1.1): THREE.Group {
  const g = new THREE.Group();
  g.add(box(w + 0.12, 0.98, 0.04, mat('#f2ede0'), 0, 1.55, 0));
  g.add(mesh(new THREE.PlaneGeometry(w, 0.84), pane, 0, 1.55, 0.025, { cast: false }));
  const c = mat(curtain, { rough: 1 });
  for (const s of [-1, 1]) g.add(box(0.28, 1.1, 0.03, c, s * (w / 2 + 0.02), 1.52, 0.06, { cast: false }));
  g.add(cyl(0.015, 0.015, w + 0.7, M.chrome, 0, 2.1, 0.07, 6).rotateZ(Math.PI / 2));
  return g;
}

// --- interior decor ------------------------------------------------------------------

function framed(tex: THREE.Texture, w: number, h: number, frame = '#c9a54a'): THREE.Group {
  const g = new THREE.Group();
  g.add(box(w + 0.12, h + 0.12, 0.04, mat(frame, { metal: 0.4, rough: 0.4 }), 0, 0, 0.02));
  g.add(mesh(new THREE.PlaneGeometry(w, h), texMat(tex), 0, 0, 0.045, { cast: false }));
  return g;
}

export function dogsPokerPainting(): THREE.Group {
  const tex = signTexture({
    w: 384,
    h: 256,
    bg: '#14101c',
    fg: '#e8c070',
    gap: 0,
    lines: [
      { text: ':dog: :cards: :dog:', size: 92, font: 'Rye' },
      { text: ':dog: :cash: :poodle:', size: 78, font: 'Rye' },
    ],
  });
  return framed(tex, 1.0, 0.68);
}

export function wolfTapestry(): THREE.Group {
  const tex = signTexture({
    w: 256,
    h: 320,
    bg: '#16223d',
    fg: '#d9e2f2',
    gap: 6,
    lines: [
      { text: ':full-moon:', size: 90, font: 'Rye' },
      { text: ':wolf::wolf::wolf:', size: 70, font: 'Rye' },
      { text: 'HOWL', size: 40, font: 'Rye', color: '#8aa0c8' },
    ],
  });
  const g = new THREE.Group();
  g.add(mesh(new THREE.PlaneGeometry(1.1, 1.38), texMat(tex), 0, 0, 0.01, { cast: false }));
  g.add(cyl(0.015, 0.015, 1.25, mat('#6b4a2b'), 0, 0.71, 0.02, 6).rotateZ(Math.PI / 2));
  return g;
}

export function deerMount(): THREE.Group {
  const g = new THREE.Group();
  const fur = mat('#8a5a33', { rough: 1 });
  g.add(box(0.5, 0.62, 0.05, cabinet(), 0, 0, 0.025));
  g.add(box(0.3, 0.34, 0.32, fur, 0, -0.04, 0.2));
  g.add(box(0.2, 0.16, 0.28, fur, 0, -0.12, 0.46));
  g.add(box(0.09, 0.07, 0.04, M.black, 0, -0.11, 0.615, { cast: false }));
  for (const s of [-1, 1]) {
    g.add(sphere(0.03, M.black, s * 0.12, 0.02, 0.36, 6));
    g.add(box(0.06, 0.16, 0.04, fur, s * 0.17, 0.12, 0.2).rotateZ(s * -0.6));
    const beam = cyl(0.02, 0.025, 0.42, mat('#e9dcc0'), s * 0.12, 0.33, 0.18, 5);
    beam.rotation.z = s * -0.35;
    g.add(beam);
    for (const t of [0.12, 0.26]) {
      const tine = cyl(0.012, 0.016, 0.16, mat('#e9dcc0'), s * (0.13 + t * 0.3), 0.26 + t, 0.18, 4);
      tine.rotation.z = s * 0.4;
      g.add(tine);
    }
  }
  return g;
}

export function singingBass(): { group: THREE.Group; head: THREE.Mesh } {
  const g = new THREE.Group();
  g.add(box(0.7, 0.32, 0.04, cabinet(), 0, 0, 0.02));
  const green = mat('#5f7f3a', { rough: 0.5, metal: 0.2 });
  const head = mesh(new THREE.BoxGeometry(0.2, 0.18, 0.08), green, 0.18, 0, 0.09);
  g.add(head);
  g.add(box(0.32, 0.16, 0.07, green, -0.06, 0, 0.085));
  const tail = box(0.12, 0.2, 0.04, green, -0.27, 0, 0.08);
  g.add(tail);
  g.add(sphere(0.02, M.black, 0.24, 0.04, 0.135, 6));
  g.add(box(0.1, 0.06, 0.03, mat('#c0392b'), 0, -0.13, 0.055, { cast: false }));
  return { group: g, head };
}

export function neonBeerSign(): THREE.Group {
  const tex = signTexture({
    w: 512,
    h: 192,
    bg: 'rgba(0,0,0,0)',
    fg: '#ff4fa3',
    gap: 2,
    lines: [
      { text: 'COLD', size: 80, font: 'Bangers', color: '#5ff3ff' },
      { text: 'BEER', size: 90, font: 'Bangers', color: '#ff4fa3' },
    ],
  });
  const g = new THREE.Group();
  g.add(box(1.1, 0.46, 0.03, mat('#151515'), 0, 0, 0.015));
  const neon = new THREE.MeshStandardMaterial({
    map: tex,
    transparent: true,
    emissive: new THREE.Color('#ffffff'),
    emissiveMap: tex,
    emissiveIntensity: 1.6,
    depthWrite: false,
  });
  g.add(mesh(new THREE.PlaneGeometry(1.0, 0.38), neon, 0, 0, 0.04, { cast: false }));
  return g;
}

export function dreamCatcher(): THREE.Group {
  const g = new THREE.Group();
  const hoop = mesh(new THREE.TorusGeometry(0.18, 0.012, 6, 20), mat('#8a5a33'), 0, 0, 0.03);
  g.add(hoop);
  g.add(
    mesh(
      new THREE.CircleGeometry(0.17, 12),
      mat('#e9dcc0', { side: THREE.DoubleSide, transparent: true, opacity: 0.5 }),
      0,
      0,
      0.032,
      { cast: false },
    ),
  );
  for (const x of [-0.1, 0, 0.1]) {
    g.add(cyl(0.004, 0.004, 0.22, mat('#6b4a2b'), x, -0.29, 0.03, 4));
    g.add(
      box(
        0.05,
        0.13,
        0.01,
        mat(['#7aa6c8', '#e8c070', '#c75b6b'][(x * 10 + 1) | 0] ?? '#e8c070'),
        x,
        -0.44,
        0.03,
        { cast: false },
      ),
    );
  }
  return g;
}

export function beerFridge(): THREE.Group {
  const g = new THREE.Group();
  const red = mat('#b52025', { rough: 0.4, metal: 0.3 });
  g.add(box(0.6, 0.9, 0.6, red, 0, 0.45, 0));
  g.add(box(0.04, 0.4, 0.05, M.chrome, -0.24, 0.55, 0.32, { cast: false }));
  const tex = signTexture({
    w: 256,
    h: 128,
    bg: '#ffffff',
    fg: '#b52025',
    lines: [{ text: 'BEER', size: 90, font: 'Bangers' }],
  });
  g.add(mesh(new THREE.PlaneGeometry(0.4, 0.2), texMat(tex), 0.03, 0.62, 0.305, { cast: false }));
  return g;
}

export function lavaLamp(): { group: THREE.Group; blobs: THREE.Mesh[] } {
  const g = new THREE.Group();
  g.add(box(0.5, 0.6, 0.4, cabinet(), 0, 0.3, 0));
  g.add(cyl(0.08, 0.1, 0.12, mat('#c9a54a', { metal: 0.6 }), 0, 0.66, 0, 10));
  const glow = new THREE.MeshStandardMaterial({
    color: '#ff7a3d',
    emissive: new THREE.Color('#ff5a1f'),
    emissiveIntensity: 0.8,
    transparent: true,
    opacity: 0.55,
  });
  g.add(mesh(new THREE.CylinderGeometry(0.05, 0.09, 0.38, 10), glow, 0, 0.91, 0, { cast: false }));
  g.add(cyl(0.05, 0.06, 0.06, mat('#c9a54a', { metal: 0.6 }), 0, 1.13, 0, 10));
  const blobMat = new THREE.MeshStandardMaterial({
    color: '#ffd23b',
    emissive: new THREE.Color('#ffb000'),
    emissiveIntensity: 1,
  });
  const blobs = [0, 1].map((i) => {
    const b = mesh(new THREE.SphereGeometry(0.035 + i * 0.01, 8, 6), blobMat, 0, 0.8 + i * 0.15, 0, {
      cast: false,
    });
    g.add(b);
    return b;
  });
  return { group: g, blobs };
}

export function fishTank(): { group: THREE.Group; fish: THREE.Mesh } {
  const g = new THREE.Group();
  g.add(box(1.0, 0.7, 0.45, cabinet(), 0, 0.35, 0));
  const glass = mat('#9fd4e6', { transparent: true, opacity: 0.35, rough: 0.05 });
  g.add(mesh(new THREE.BoxGeometry(0.9, 0.5, 0.38), glass, 0, 0.96, 0, { cast: false }));
  g.add(box(0.86, 0.06, 0.34, mat('#c9b48a'), 0, 0.74, 0, { cast: false }));
  g.add(box(0.94, 0.04, 0.42, M.black, 0, 1.23, 0, { cast: false }));
  const fish = mesh(new THREE.BoxGeometry(0.1, 0.06, 0.03), mat('#ff8a1f', { rough: 0.4 }), 0, 0.95, 0, {
    cast: false,
  });
  g.add(fish);
  // A tiny castle. Kevin's.
  g.add(box(0.12, 0.14, 0.12, mat('#9a9aa0'), 0.25, 0.84, 0.05, { cast: false }));
  return { group: g, fish };
}

export function karaokeMachine(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.5, 0.75, 0.4, M.black, 0, 0.375, 0));
  for (const y of [0.25, 0.55])
    g.add(cyl(0.13, 0.13, 0.02, mat('#333338'), 0, y, 0.205, 12, { cast: false }).rotateX(Math.PI / 2));
  g.add(cyl(0.015, 0.015, 1.3, M.chrome, 0.55, 0.65, 0.2, 6));
  g.add(sphere(0.045, mat('#222'), 0.55, 1.34, 0.2, 8));
  g.add(cyl(0.18, 0.18, 0.02, M.black, 0.55, 0.01, 0.2, 10));
  return g;
}

export function tanningBed(): { group: THREE.Group; glow: THREE.MeshStandardMaterial } {
  const g = new THREE.Group();
  g.add(box(2.0, 0.5, 0.95, mat('#f4f4f4', { rough: 0.3 }), 0, 0.25, 0));
  const glow = new THREE.MeshStandardMaterial({
    color: '#c9a8ff',
    emissive: new THREE.Color('#9b5cff'),
    emissiveIntensity: 0.4,
  });
  g.add(box(1.9, 0.04, 0.85, glow, 0, 0.52, 0, { cast: false }));
  const lid = box(2.0, 0.2, 0.95, mat('#f4f4f4', { rough: 0.3 }), 0, 0.82, -0.25);
  lid.rotation.x = -0.6;
  g.add(lid);
  return { group: g, glow };
}

export function gameConsole(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.8, 0.45, 0.45, cabinet(), 0, 0.225, 0));
  g.add(box(0.5, 0.42, 0.42, mat('#2b2b2e'), 0, 0.66, 0));
  g.add(
    mesh(
      new THREE.PlaneGeometry(0.38, 0.3),
      mat('#2a6a8a', { emissive: '#3a9ad0', emissiveIntensity: 0.6 }),
      -0.02,
      0.68,
      0.215,
      { cast: false },
    ),
  );
  g.add(box(0.3, 0.06, 0.16, M.black, 0, 0.48, 0.12, { cast: false }));
  g.add(box(0.14, 0.04, 0.09, mat('#3a3a40'), 0.18, 0.016, 0.45));
  return g;
}

export function canPyramid(rng: Random): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.9, 0.5, 0.4, cabinet(), 0, 0.25, 0));
  const colors = ['#c0392b', '#2b6cb0', '#d9d4c7', '#c0392b'];
  for (let row = 0; row < 4; row++)
    for (let i = 0; i < 4 - row; i++) {
      const x = (i - (3 - row) / 2) * 0.17;
      g.add(
        cyl(
          0.075,
          0.075,
          0.2,
          mat(rng.pick(colors), { metal: 0.6, rough: 0.3 }),
          x,
          0.6 + row * 0.205,
          0,
          10,
        ),
      );
    }
  return g;
}

export function bigRecliner(): THREE.Group {
  const g = new THREE.Group();
  const m = mat('#5a3a24', { rough: 0.6 });
  g.add(box(1.0, 0.5, 0.95, m, 0, 0.27, 0));
  const back = box(1.0, 0.85, 0.28, m, 0, 0.82, -0.45);
  back.rotation.x = -0.3;
  g.add(back);
  for (const s of [-1, 1]) {
    g.add(box(0.22, 0.32, 0.9, m, s * 0.5, 0.62, 0));
    g.add(cyl(0.045, 0.045, 0.04, M.black, s * 0.5, 0.8, 0.3, 8));
  }
  const foot = box(0.85, 0.12, 0.55, m, 0, 0.36, 0.7);
  foot.rotation.x = 0.25;
  g.add(foot);
  return g;
}

/** Night glow helper for indoor screens and lamps. */
export function glowAtNight(m: THREE.MeshStandardMaterial, strength: number): void {
  registerNightGlow(m, strength);
}
